/**
 * JP formatting-preserving diff highlighter.
 *
 * Shows a version diff of a JP section WITHOUT losing formatting: it tokenizes
 * each side's HTML into TAGS (kept verbatim) + TEXT UNITS, runs a POSITIONAL
 * diff over just the text units, and wraps only the changed text units in
 * red/green spans. Because tags pass through untouched, tables / lists / bold /
 * links all survive — and because the diff is positional (not the set-membership
 * approach XmlDiffHighlighter uses), it highlights exactly the changed run, with
 * no over-highlighting.
 *
 * Diff is authoritative on the JAPANESE source (decision 2026-09-26): the JP
 * label is Japanese and the English is a per-version translation produced at a
 * different time, so diffing the English would surface translation drift as
 * phantom red/green. The compare view therefore diffs ONLY the Japanese and
 * feeds this component the Japanese HTML pair with ``charLevel`` (char
 * granularity — Japanese has no word spaces, and JS `\w` excludes CJK). Reading
 * a version in English lives in the main viewer, not here.
 */
import React, { useMemo } from 'react';
import { diffArrays } from 'diff';
import DOMPurify from 'dompurify';

interface Token {
  tag: boolean;
  text: string;
}

/** Split HTML into tag tokens (verbatim) + text-unit tokens (word or char). */
export function tokenizeHtml(html: string, charLevel: boolean): Token[] {
  const out: Token[] = [];
  // Our content_html is generated by our own parser (clean, well-formed), so a
  // tag/text split is safe here (mirrors the assumption XmlDiffHighlighter makes).
  for (const piece of (html || '').split(/(<[^>]+>)/)) {
    if (!piece) continue;
    if (piece.startsWith('<') && piece.endsWith('>')) {
      out.push({ tag: true, text: piece });
      continue;
    }
    const units = charLevel
      ? Array.from(piece)                       // per character (JA)
      : piece.match(/(\s+|[^\s]+)/g) || [];     // words + whitespace runs (EN)
    for (const u of units) out.push({ tag: false, text: u });
  }
  return out;
}

/**
 * Positional diff of the two sides' text units → boolean masks marking which
 * OLD units were removed and which NEW units were added (index-aligned to each
 * side's non-tag tokens, in order).
 */
export function computeMasks(oldUnits: string[], newUnits: string[]): {
  oldRemoved: boolean[];
  newAdded: boolean[];
} {
  const parts = diffArrays(oldUnits, newUnits);
  const oldRemoved: boolean[] = [];
  const newAdded: boolean[] = [];
  for (const part of parts) {
    const n = part.value.length;
    if (part.added) {
      for (let i = 0; i < n; i++) newAdded.push(true);
    } else if (part.removed) {
      for (let i = 0; i < n; i++) oldRemoved.push(true);
    } else {
      for (let i = 0; i < n; i++) {
        oldRemoved.push(false);
        newAdded.push(false);
      }
    }
  }
  return { oldRemoved, newAdded };
}

function render(tokens: Token[], mask: boolean[], cls: string): string {
  let out = '';
  let ti = 0;
  for (const t of tokens) {
    if (t.tag) {
      out += t.text;
      continue;
    }
    const changed = mask[ti++];
    // never wrap pure whitespace (avoids stray highlighted gaps)
    if (changed && t.text.trim()) out += `<span class="${cls}">${t.text}</span>`;
    else out += t.text;
  }
  return out;
}

/**
 * Build one side's diff HTML. ``side='old'`` marks removed text (red);
 * ``side='new'`` marks added text (green). Tags are preserved verbatim, so the
 * output is valid HTML with the original formatting intact.
 */
export function buildJpDiffHtml(
  oldHtml: string,
  newHtml: string,
  side: 'old' | 'new',
  charLevel: boolean,
): string {
  const oldTokens = tokenizeHtml(oldHtml || '', charLevel);
  const newTokens = tokenizeHtml(newHtml || '', charLevel);
  const oldUnits = oldTokens.filter((t) => !t.tag).map((t) => t.text);
  const newUnits = newTokens.filter((t) => !t.tag).map((t) => t.text);
  const { oldRemoved, newAdded } = computeMasks(oldUnits, newUnits);
  return side === 'old'
    ? render(oldTokens, oldRemoved, 'diff-removed')
    : render(newTokens, newAdded, 'diff-added');
}

const ALLOWED_TAGS = [
  'p', 'br', 'span', 'div', 'strong', 'b', 'em', 'i', 'u',
  'ul', 'ol', 'li', 'table', 'caption', 'thead', 'tbody', 'tr', 'th', 'td',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'sup', 'sub', 'a',
];
const ALLOWED_ATTR = ['class', 'href', 'colspan', 'rowspan', 'id'];

interface JpDiffHighlighterProps {
  oldHtml: string | null;
  newHtml: string | null;
  /** true = Japanese (character-level); false = English (word-level). */
  charLevel: boolean;
  className?: string;
}

/**
 * Side-by-side previous/current panes with formatting preserved and inline
 * red/green highlighting. Per-side highlighting only ever ADDS spans around
 * text, so the HTML stays valid.
 */
export const JpDiffHighlighter: React.FC<JpDiffHighlighterProps> = ({
  oldHtml, newHtml, charLevel, className = '',
}) => {
  const { oldOut, newOut } = useMemo(() => {
    return {
      oldOut: DOMPurify.sanitize(
        buildJpDiffHtml(oldHtml || '', newHtml || '', 'old', charLevel),
        { ALLOWED_TAGS, ALLOWED_ATTR }),
      newOut: DOMPurify.sanitize(
        buildJpDiffHtml(oldHtml || '', newHtml || '', 'new', charLevel),
        { ALLOWED_TAGS, ALLOWED_ATTR }),
    };
  }, [oldHtml, newHtml, charLevel]);

  return (
    <div className={`grid grid-cols-2 gap-4 ${className}`}>
      <div className="border rounded p-3 bg-white overflow-x-auto">
        <div className="prose prose-sm max-w-none text-slate-700 text-sm leading-relaxed"
             dangerouslySetInnerHTML={{ __html: oldOut || '<em class="text-slate-400">(not in this version)</em>' }} />
      </div>
      <div className="border rounded p-3 bg-white overflow-x-auto">
        <div className="prose prose-sm max-w-none text-slate-700 text-sm leading-relaxed"
             dangerouslySetInnerHTML={{ __html: newOut || '<em class="text-slate-400">(removed in this version)</em>' }} />
      </div>
    </div>
  );
};

export default JpDiffHighlighter;
